/**
 * Prop Pick Adapter
 *
 * Transforms admin preview data into PropPick component props
 *
 * PropPick displays a team matchup (two jerseys) and a yes/no question.
 * In preview mode without full match data, we create mock teams from
 * available option images or use placeholders.
 */

import { createTeamsFromOptions } from '@low6-pickem/ui/utils';
import type { PreviewQuestionData } from '../types';

/**
 * PropPick component props (from packages/ui/src/components/PropPick.tsx)
 */
interface PropPickProps {
  teams: [any, any]; // Tuple of exactly 2 teams for matchup display
  question: string;
  subtext?: string;
  selectedAnswer?: 'yes' | 'no';
  onAnswerSelect: (answer: 'yes' | 'no') => void;
  points: string | number;
  earnedPoints?: number;
  percentages?: { yes: number; no: number };
  isCorrect?: boolean;
  disabled?: boolean;
  className?: string;
  questionId?: string;
  questionNumber?: number;
  thirdImages?: { yes?: string | null; no?: string | null };
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

/**
 * Transforms preview data into PropPick props
 *
 * Creates two mock teams for the matchup display and identifies
 * which options represent "yes" and "no" answers.
 *
 * @param data - Preview question data from admin form
 * @returns PropPick component props
 */
export function propPickAdapter(data: PreviewQuestionData): PropPickProps {
  const language = data.language || 'en';

  // Create mock teams for matchup display (if we have at least 2 options, use their images)
  // Otherwise, create placeholder teams
  const teamsForMatchup =
    data.options.length >= 2
      ? createTeamsFromOptions(data.options.slice(0, 2), language)
      : [
          {
            id: 'team-1',
            name: 'Team 1',
            color: '#c8102e',
            accent: '#fff',
            jerseyUrl: undefined,
          },
          {
            id: 'team-2',
            name: 'Team 2',
            color: '#003f7f',
            accent: '#fff',
            jerseyUrl: undefined,
          },
        ];

  // Extract third images (jersey images) from yes/no options
  // Typically prop questions have 2 options: Yes and No
  // third_image fields are for the jersey images on Yes/No buttons
  // option_image fields are for the team matchup display (handled by createTeamsFromOptions)
  const thirdImages =
    data.options.length >= 2
      ? {
          yes: data.options[0][language === 'en' ? 'third_image_en' : 'third_image_fr'],
          no: data.options[1][language === 'en' ? 'third_image_en' : 'third_image_fr'],
        }
      : undefined;

  return {
    teams: [teamsForMatchup[0], teamsForMatchup[1]], // Exactly 2 teams as tuple
    question: data.questionText,
    subtext: data.questionSubtext,
    selectedAnswer: undefined, // No selection in preview
    onAnswerSelect: () => {}, // No-op handler (preview is view-only)
    points: data.totalPoints,
    thirdImages,
    disabled: true, // Preview is view-only
    questionId: 'preview',
    questionNumber: undefined, // No question number in preview
  };
}
